import AddIcon from '@mui/icons-material/Add';
import CachedIcon from '@mui/icons-material/Cached';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import {
    Badge,
    Box,
    Button,
    Card,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    Popover,
    Radio,
    RadioGroup,
    Stack,
    StackProps,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import distance from '@turf/distance';
import { chain, filter, isNumber, reduce, sortBy, uniqBy } from 'lodash';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { Link, useNavigate } from 'react-router-dom';

import { useDeviceIdList } from '@/api/deviceIdList';
import { Device, UseDevicesOptions, useDevices } from '@/api/devices';
import { useIsAdmin, useUserAccount } from '@/api/userAccount';
import { Null } from '@/components/Null';
import { ScrollToPagePosition } from '@/components/ScrollToPagePosition';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { SELECTED_SPOTTER_SUMMARY_CARD_SX, SpotterSummaryCard } from '@/components/map/SpotterSummaryCard';
import { InlineAlert, InlineAlertSecondaryButton } from '@/components/ui/InlineAlert';
import { INACTIVE_SPOTTER_MS } from '@/config';
import {
    SPOTTER_LIST_FILTERS,
    SpotterListFilter,
    SpotterListOptions,
    SpotterListSort,
    useMapUiContext,
} from '@/context/MapUiContext';
import { useGeoLocation } from '@/hooks/useGeoLocation';
import { useNow } from '@/hooks/useNow';
import { spotterIdHash, useSpotterHash } from '@/hooks/useSpotterHash';
import { minorTextGrey } from '@/theme';
import { equalsShallow } from '@/utils';

export type SpotterListProps = StackProps & {
    /**
     * Called by SpotterList to inform containiner when a spotter
     * becomes hovered (or undefined on leaving hover)
     */
    onSpotterHover?: (spotterId: string | undefined) => void;
    /**
     * Spotter that should be given hover treatment
     */
    hoveredSpotterId?: string | undefined;
};

/**
 * Fleet size at which the list defaults to the "Active" filter. Exported so a test can pad a fixture to
 * exactly this many devices instead of guessing a number that has to stay above it.
 */
export const AUTO_ACTIVE_FILTER_MIN_DEVICES = 100;

const lastUpdate = (device: Device) => device.lastMessageTime ?? new Date(device.timeLastUpdatedUTC ?? 0).getTime();

export const useSpotterListFilterFns = (): Record<SpotterListFilter, (device: Device) => boolean> => {
    const now = useNow(500);

    return useMemo(
        () => ({
            all: () => true,
            active: (device) => lastUpdate(device) > now.getTime() - INACTIVE_SPOTTER_MS,
        }),
        [now]
    );
};

export const MapSpotterList = ({ hoveredSpotterId, onSpotterHover, ...props }: SpotterListProps) => {
    const {
        spotterListOptions: { sort: spotterSort, filter: spotterFilter },
        setSpotterListOptions,
    } = useMapUiContext();
    const isAdmin = useIsAdmin();
    const [searchText, setSearchText] = useState('');
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [frozenLastUpdates, setFrozenLastUpdates] = useState<Record<string, number>>();

    const navigate = useNavigate();
    const { coords } = useGeoLocation();
    const mixpanel = useMixpanel();

    const { spotterId: selectedSpotterId } = useSpotterHash();

    const { data: deviceIdItems, isPending } = useDeviceIdList();
    const deviceList = useMemo(() => {
        if (!isAdmin) {
            // returning this directly if we're not doing any of the special admin stuff so that consumers don't react to every searchText change
            return deviceIdItems?.data?.devices ?? [];
        }

        const deviceList: UseDevicesOptions['devices'] = [...(deviceIdItems?.data?.devices ?? [])];
        if (searchText.match(/[A-Z]+-\w{4,}/)) {
            deviceList.push({ spotterId: searchText.trim() });
        }
        if (selectedSpotterId) {
            deviceList.push({ spotterId: selectedSpotterId });
        }
        return uniqBy(deviceList, 'spotterId');
    }, [deviceIdItems?.data?.devices, isAdmin, searchText, selectedSpotterId]);

    const { data: devices = [], isFetching } = useDevices({
        devices: deviceList,
    });

    const [isScrolled, setIsScrolled] = useState(false);
    const [hasDefaultedFilter, setHasDefaultedFilter] = useState(false);

    useEffect(() => {
        if (spotterSort !== 'lastUpdate' || (!isScrolled && !selectedSpotterId)) {
            setFrozenLastUpdates(undefined);
            return;
        }
        if (!frozenLastUpdates) {
            setFrozenLastUpdates(Object.fromEntries(devices.map((d) => [d.spotterId, lastUpdate(d)])));
        }
    }, [devices, frozenLastUpdates, isScrolled, selectedSpotterId, spotterSort]);

    /**
     * Effect: The first time we see at least AUTO_ACTIVE_FILTER_MIN_DEVICES spotters, default the filter
     * to "Active", mostly for Grid's benefit.
     */
    useEffect(() => {
        if (!hasDefaultedFilter && (deviceIdItems?.data.devices.length ?? 0) >= AUTO_ACTIVE_FILTER_MIN_DEVICES) {
            setSpotterListOptions({ filter: 'active' });
            setHasDefaultedFilter(true);
        }
    }, [deviceIdItems?.data.devices.length, hasDefaultedFilter, setSpotterListOptions]);

    const filterFns = useSpotterListFilterFns();
    const currentFilterFn = filterFns[spotterFilter];

    const filterCounts = useMemo(() => {
        if ((devices?.length ?? 0) < (deviceIdItems?.data.devices.length ?? 0) && isFetching) {
            // we're probably just showing a placeholder out of the cache, so the numbers will be misleading, don't show them until we have all the devices
            return {};
        }
        return reduce(
            devices,
            (counts, device) =>
                Object.fromEntries(
                    SPOTTER_LIST_FILTERS.map((f) => [f, (counts[f] ?? 0) + (filterFns[f](device) ? 1 : 0)])
                ),
            {} as FilterMenuProps['counts']
        );
    }, [deviceIdItems?.data.devices.length, devices, filterFns, isFetching]);

    const filteredByTermDevices = useMemo(
        () =>
            filter(devices ?? [], (deviceToFilter) => {
                if (!searchText.trim().length) return true;
                const searchTerm = searchText.toLowerCase();

                return (
                    deviceToFilter.name?.toLowerCase().includes(searchTerm) ||
                    deviceToFilter.spotterId?.toLowerCase().includes(searchTerm)
                );
            }),
        [devices, searchText]
    );

    const searchTextFoundUniqueSpotter = useMemo(
        () => searchText.trim().length >= 4 && filteredByTermDevices.length === 1,
        [filteredByTermDevices, searchText]
    );

    /**
     * Effect: if exactly one spotter matches the search terms (and enough has been typed for it to feel identifying),
     * select it so we zoom over there and so it's not subject to the filter (although it'll get the "doesn't match
     * the filter" banner).
     */
    useEffect(() => {
        if (searchTextFoundUniqueSpotter && filteredByTermDevices[0].spotterId !== selectedSpotterId) {
            navigate(`#${spotterIdHash(filteredByTermDevices[0].spotterId)}`);
        }
    }, [filteredByTermDevices, navigate, searchTextFoundUniqueSpotter, selectedSpotterId]);

    const filteredDevices = useMemo(
        () =>
            chain(filteredByTermDevices ?? [])
                .filter((d) => currentFilterFn(d) || d.spotterId === selectedSpotterId)
                .sortBy((deviceToSort) => {
                    switch (spotterSort) {
                        case 'lastUpdate':
                            return -(frozenLastUpdates?.[deviceToSort.spotterId] ?? lastUpdate(deviceToSort));
                        case 'name':
                            return deviceToSort.name?.toLowerCase() || deviceToSort.spotterId?.toLowerCase();
                        case 'proximity': {
                            const deviceLocation = deviceToSort.track?.[0];
                            if (!coords || !deviceLocation) {
                                return Number.MAX_VALUE;
                            }
                            return distance(
                                [coords.longitude, coords.latitude],
                                [deviceLocation.longitude, deviceLocation.latitude]
                            );
                        }
                    }
                })
                .value(),
        [filteredByTermDevices, currentFilterFn, selectedSpotterId, spotterSort, frozenLastUpdates, coords]
    );

    const matchTermButNotFilterCount = useMemo(() => {
        if (searchTextFoundUniqueSpotter) {
            // it's going to end up shown by virtue of getting autoselected, so avoid some flicker
            return 0;
        }

        return filteredByTermDevices.length - filteredDevices.length;
    }, [filteredByTermDevices.length, filteredDevices.length, searchTextFoundUniqueSpotter]);

    // Either initial load, or we found a few devices in the cache but we're fetching more.
    const loadingMoreSpotters =
        isPending || (isFetching && (devices?.length ?? 0) < (deviceIdItems?.data.devices.length ?? 0));

    function onChangeSearchText(value: string) {
        setSearchText(value);
    }

    const isOutOfSort = useMemo(
        () =>
            !!frozenLastUpdates &&
            !equalsShallow(
                sortBy(devices, (d) => lastUpdate(d)),
                sortBy(devices, (d) => frozenLastUpdates[d.spotterId] ?? lastUpdate(d))
            ),
        [devices, frozenLastUpdates]
    );

    return (
        <Stack {...props}>
            {!loadingMoreSpotters && <ScrollToPagePosition scrollToTop={false} />}
            <Stack
                direction="row"
                sx={{
                    boxShadow: isScrolled ? 2 : 'none',
                    transition: 'box-shadow 0.2s',
                    zIndex: 1,
                    overflow: 'initial',
                    p: 2,
                    pr: 3,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                    <SortMenu
                        value={spotterSort}
                        isOutOfSort={isOutOfSort}
                        onResort={() => {
                            mixpanel?.track('Spotter list refresh sort');
                            setFrozenLastUpdates(undefined);
                            scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        onChange={(sort) => {
                            mixpanel?.track('Spotter list sort', { sort });
                            setSpotterListOptions({ sort });
                        }}
                    />
                    <FilterMenu
                        value={spotterFilter}
                        counts={filterCounts}
                        onChange={(filter) => {
                            mixpanel?.track('Spotter list filter', { filter });
                            setSpotterListOptions({ filter });
                        }}
                    />
                </Stack>
                <TextField
                    data-testid="spotter-list-search-input"
                    sx={{ flexGrow: 1, maxWidth: '240px' }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        },
                    }}
                    size="small"
                    variant="standard"
                    value={searchText}
                    placeholder="Search Spotter name or ID"
                    onChange={(e) => onChangeSearchText(e.target.value)}
                />
            </Stack>
            {!isAdmin && deviceIdItems?.data?.devices && !deviceIdItems.data.devices.length && (
                <Stack sx={{ mx: 'auto', my: 1, justifyContent: 'center', gap: 2 }}>
                    <Typography color={minorTextGrey}>
                        You don&apos;t have any Spotters registered to your account.
                    </Typography>
                    <Button
                        component={Link}
                        sx={{ m: 'auto' }}
                        to="/setup/register"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => mixpanel?.track('Click register from empty spotter list')}
                    >
                        Register Spotter
                    </Button>
                </Stack>
            )}
            {isAdmin && <AdminMessage />}
            <Box
                sx={{ overflow: 'auto', px: 1, py: '1px' }}
                onScroll={(e) => setIsScrolled((e.target as HTMLDivElement).scrollTop > 0)}
                ref={scrollContainerRef}
            >
                <Stack sx={{ gap: 1 }}>
                    {filteredDevices.map((device) => (
                        <Stack key={device.spotterId}>
                            {!currentFilterFn(device) && (
                                <InlineAlert
                                    severity="info"
                                    sx={{
                                        position: 'relative',
                                        boxSizing: 'border-box',
                                        ...SELECTED_SPOTTER_SUMMARY_CARD_SX,
                                        top: '1px',
                                        mt: '-2px',
                                        borderBottomLeftRadius: 0,
                                        borderBottomRightRadius: 0,
                                        borderBottom: 'none',
                                        py: 0.5,
                                        pr: 0.5,
                                        pl: 1.5,
                                    }}
                                >
                                    The selected Spotter does not match the current filter
                                </InlineAlert>
                            )}
                            <SpotterSummaryCard
                                device={device}
                                isHovered={hoveredSpotterId === device.spotterId}
                                onHoverChange={(hovered) => onSpotterHover?.(hovered ? device.spotterId : undefined)}
                            />
                        </Stack>
                    ))}
                    {loadingMoreSpotters && (
                        <Card variant="outlined">
                            <GenericSkeleton sx={{ p: 2 }} />
                        </Card>
                    )}
                    {!loadingMoreSpotters && (
                        <ShowHiddenSpottersAlert
                            matchTermButNotFilterCount={matchTermButNotFilterCount}
                            matchTermAndFilterCount={filteredDevices.length}
                            searchText={searchText}
                            setSpotterListOptions={setSpotterListOptions}
                        />
                    )}
                </Stack>
            </Box>
        </Stack>
    );
};

const AdminMessage = () => {
    const { data: accountData } = useUserAccount();
    const name = accountData?.data?.user
        ? `${accountData.data.user.firstName} ${accountData.data.user.lastName}`
        : 'Administrator';

    return (
        <Stack sx={{ mx: 'auto', my: 1, justifyContent: 'center', gap: 2 }}>
            <Typography color={minorTextGrey} sx={{ mx: 1 }}>
                Hi {name}! Try entering a complete SPOT-id in the{' '}
                <SearchIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                box above!
            </Typography>
        </Stack>
    );
};

const ShowHiddenSpottersAlert = ({
    setSpotterListOptions,
    searchText,
    matchTermButNotFilterCount,
    matchTermAndFilterCount,
}: {
    setSpotterListOptions: (value: Partial<SpotterListOptions>) => void;
    searchText: string | null;
    matchTermButNotFilterCount: number;
    matchTermAndFilterCount: number;
}) => {
    if (!matchTermButNotFilterCount) {
        // no spotters hidden, nothing to alert
        return <Null data-testid="no-hidden-spotter-message" />;
    }
    if (!searchText && matchTermAndFilterCount) {
        // there's no search text and we have some visible spotters, so no need to do anything special here; just a normal filter.
        return <Null data-testid="no-hidden-spotter-message" />;
    }

    return (
        <InlineAlert
            data-testid="hidden-spotter-message"
            severity="info"
            sx={{
                boxSizing: 'border-box',
                py: 0.5,
                pr: 0.5,
                pl: 1.5,
            }}
            actionButton={
                <InlineAlertSecondaryButton onClick={() => setSpotterListOptions({ filter: 'all' })}>
                    Show hidden Spotters
                </InlineAlertSecondaryButton>
            }
        >
            {searchText && (
                <>
                    {matchTermButNotFilterCount === 1 ? (
                        <>{matchTermButNotFilterCount} Spotter matches</>
                    ) : (
                        <>{matchTermButNotFilterCount} Spotters match</>
                    )}{' '}
                    &quot;{searchText}&quot; but not the current filter
                </>
            )}
            {!searchText && <>All Spotters are hidden by the current filter</>}
        </InlineAlert>
    );
};

type SortMenuProps = {
    value: SpotterListSort;
    isOutOfSort: boolean;
    onResort: () => void;
    onChange: (newValue: SpotterListSort) => void;
};

const SortMenu = ({ value, isOutOfSort = true, onResort, onChange }: SortMenuProps) => {
    const id = useId();
    const { setShowMyLocation } = useMapUiContext();
    const { coords, permission: geoLocationPermission, requestLocation } = useGeoLocation();

    const [anchorEl, setAnchorEl] = useState<Element | null>(null);

    const handleClick = (event: React.MouseEvent<Element>) => {
        if (!anchorEl) {
            setAnchorEl(event.currentTarget);
        } else {
            handleClose();
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSortChange = async (newSort: SpotterListSort) => {
        if (newSort === 'proximity' && !coords) {
            // Request location first (triggers prompt on Safari)
            try {
                await requestLocation();
                onChange(newSort);
                setShowMyLocation(true);
            } catch {
                // Permission denied or error - don't change sort
            }
        } else {
            onChange(newSort);
            if (newSort === 'proximity') {
                setShowMyLocation(true);
            }
        }
        handleClose();
    };

    const open = Boolean(anchorEl);

    return (
        <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Badge
                color="info"
                variant="dot"
                invisible={!isOutOfSort}
                sx={{ '.MuiBadge-dot': { top: '10px', right: '3px' } }}
            >
                <Button sx={{ gap: 0.5, color: isOutOfSort ? 'gray' : 'info.main' }} onClick={handleClick}>
                    <SortIcon /> Sort
                </Button>
            </Badge>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Paper
                    sx={{
                        backgroundColor: 'white',
                        p: 1,
                        pl: 2,
                    }}
                >
                    <Stack>
                        <FormControl>
                            <RadioGroup
                                defaultValue="lastUpdate"
                                value={value}
                                onChange={(event) => {
                                    handleSortChange(event.target.value as SpotterListSort);
                                }}
                            >
                                <FormControlLabel
                                    value="lastUpdate"
                                    control={<Radio />}
                                    label={
                                        <>
                                            Last update
                                            {isOutOfSort && (
                                                <>
                                                    {' '}
                                                    <Tooltip
                                                        title={
                                                            <>
                                                                Spotters have reported fresh data!
                                                                <br />
                                                                Click to bring newest to the top
                                                            </>
                                                        }
                                                    >
                                                        <IconButton
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => {
                                                                onResort?.();
                                                                handleClose();
                                                            }}
                                                        >
                                                            <CachedIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </>
                                    }
                                />
                                <FormControlLabel value="name" control={<Radio />} label="Name" />
                                {geoLocationPermission !== 'denied' && (
                                    <FormControlLabel value="proximity" control={<Radio />} label="Proximity" />
                                )}
                            </RadioGroup>
                        </FormControl>
                    </Stack>
                </Paper>
            </Popover>
        </Stack>
    );
};

type FilterMenuProps = {
    value: SpotterListFilter;
    counts: { [filter in SpotterListFilter]?: number };
    onChange: (newValue: SpotterListFilter) => void;
};

const FilterMenu = ({ value, counts, onChange }: FilterMenuProps) => {
    const id = useId();

    const [anchorEl, setAnchorEl] = useState<Element | null>(null);

    const handleClick = (event: React.MouseEvent<Element>) => {
        if (!anchorEl) {
            setAnchorEl(event.currentTarget);
        } else {
            handleClose();
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFilterChange = async (newValue: SpotterListFilter) => {
        onChange(newValue);
        handleClose();
    };

    const open = Boolean(anchorEl);

    const filterLabel = value === 'all' ? 'All Spotters' : 'Active Spotters';
    const countLabels = Object.fromEntries(
        SPOTTER_LIST_FILTERS.map((f) => [f, isNumber(counts[f]) ? ` (${counts[f]})` : ''])
    );

    return (
        <>
            <Button sx={{ gap: 0.5 }} onClick={handleClick}>
                <FilterListIcon /> {filterLabel}
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Paper
                    sx={{
                        backgroundColor: 'white',
                        p: 1,
                        pl: 2,
                    }}
                >
                    <Stack>
                        <FormControl>
                            <RadioGroup
                                defaultValue="all"
                                value={value}
                                onChange={(event) => {
                                    handleFilterChange(event.target.value as SpotterListFilter);
                                }}
                            >
                                <FormControlLabel
                                    value="all"
                                    control={<Radio />}
                                    label={`All Spotters${countLabels.all}`}
                                />
                                <FormControlLabel
                                    value="active"
                                    control={<Radio />}
                                    label={`Active in the last 24 hours${countLabels.active}`}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Stack>
                </Paper>
            </Popover>
        </>
    );
};
